// =============================================
// Step2ActionsConfig — krok 2: trzy przelaczniki.
//  (A) utworz zone + konto (liczba zon, auto-propozycja najmniej obciazonego)
//  (B) ustaw NS w AfterMarket: "z zony" (per domena) albo "wlasne NS" (>= 2, dla wszystkich)
//  (C) rekordy A @ + www na IP hostingu — edytowalne per domena, proxy ON
// =============================================
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, X } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { AllZonesState } from "@/hooks/useAllZones";
import type { TransferWizardState } from "@/hooks/useTransferWizard";
import { isValidIPv4 } from "@/services/cloudflareService";
import { AccountPicker } from "@/components/cloudflare/shared/AccountPicker";

interface Props {
  accounts: CloudflareAccount[];
  allZones: AllZonesState;
  wizard: TransferWizardState;
}

export const Step2ActionsConfig = ({ accounts, allZones, wizard }: Props) => {
  const { config, setConfig, selected } = wizard;
  const needZone = selected.filter((d) => !allZones.findZone(d.key)).length;

  const setNsAt = (i: number, v: string) => setConfig((c) => ({ ...c, setNs: { ...c.setNs, customNs: c.setNs.customNs.map((n, idx) => (idx === i ? v : n)) } }));
  const setIp = (key: string, v: string) => setConfig((c) => {
    const next = { ...c.aRecord.ipOverrides };
    if (v.trim()) next[key] = v.trim(); else delete next[key];
    return { ...c, aRecord: { ...c.aRecord, ipOverrides: next } };
  });

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* A */}
      <Card className={!config.createZone.enabled ? "opacity-70" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span>A · Utwórz zonę w Cloudflare</span>
            <Switch checked={config.createZone.enabled} onCheckedChange={(v) => setConfig((c) => ({ ...c, createZone: { ...c.createZone, enabled: v } }))} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">Dla domen bez zony ({needZone} z {selected.length}). Domeny, które zonę mają, zostaną pominięte w tym kroku.</p>
          <div className="space-y-1">
            <Label className="text-xs">Konto Cloudflare</Label>
            <AccountPicker
              accounts={accounts}
              value={config.createZone.cfAccountId ?? ""}
              onChange={(id) => setConfig((c) => ({ ...c, createZone: { ...c.createZone, cfAccountId: id } }))}
              zoneCounts={allZones.zoneCountByAccount}
              requireAccountId
              disabled={!config.createZone.enabled}
              className="w-full"
            />
            <p className="text-[11px] text-muted-foreground">Zaproponowane konto z najmniejszą liczbą zon. Konta bez Account ID nie mogą tworzyć zon.</p>
          </div>
        </CardContent>
      </Card>

      {/* B */}
      <Card className={!config.setNs.enabled ? "opacity-70" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span>B · Ustaw NS w AfterMarket</span>
            <Switch checked={config.setNs.enabled} onCheckedChange={(v) => setConfig((c) => ({ ...c, setNs: { ...c.setNs, enabled: v } }))} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <RadioGroup value={config.setNs.mode} onValueChange={(v) => setConfig((c) => ({ ...c, setNs: { ...c.setNs, mode: v as "from_zone" | "custom" } }))} disabled={!config.setNs.enabled}>
            <label className="flex items-start gap-2 cursor-pointer">
              <RadioGroupItem value="from_zone" className="mt-0.5" />
              <span><span className="font-medium">z zony</span><br /><span className="text-xs text-muted-foreground">per domena: NS istniejącej albo nowo utworzonej zony</span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <RadioGroupItem value="custom" className="mt-0.5" />
              <span><span className="font-medium">własne NS</span><br /><span className="text-xs text-muted-foreground">ta sama lista (min. 2) dla wszystkich zaznaczonych</span></span>
            </label>
          </RadioGroup>
          {config.setNs.mode === "custom" && (
            <div className="space-y-2">
              {config.setNs.customNs.map((ns, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={ns} onChange={(e) => setNsAt(i, e.target.value)} placeholder={`ns${i + 1}.example.com`} className="font-mono text-sm h-8" disabled={!config.setNs.enabled} />
                  {config.setNs.customNs.length > 2 && (
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => setConfig((c) => ({ ...c, setNs: { ...c.setNs, customNs: c.setNs.customNs.filter((_, idx) => idx !== i) } }))}><X className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" className="h-7" onClick={() => setConfig((c) => ({ ...c, setNs: { ...c.setNs, customNs: [...c.setNs.customNs, ""] } }))}><Plus className="h-3 w-3 mr-1" />Dodaj NS</Button>
            </div>
          )}
          <p className="text-[11px] text-amber-400">Zmiana NS zawsze wymaga potwierdzenia z liczbą domen. Domeny spoza konta AfterMarket dostaną NS do ustawienia ręcznie.</p>
        </CardContent>
      </Card>

      {/* C */}
      <Card className={!config.aRecord.enabled ? "opacity-70" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between gap-2">
            <span>C · Rekordy A (@ + www)</span>
            <Switch checked={config.aRecord.enabled} onCheckedChange={(v) => setConfig((c) => ({ ...c, aRecord: { ...c.aRecord, enabled: v } }))} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <Switch id="cf-proxied" checked={config.aRecord.proxied} onCheckedChange={(v) => setConfig((c) => ({ ...c, aRecord: { ...c.aRecord, proxied: v } }))} disabled={!config.aRecord.enabled} />
            <Label htmlFor="cf-proxied" className="text-xs">Proxy Cloudflare (pomarańczowa chmurka)</Label>
          </div>
          <div className="rounded-md border max-h-64 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead className="text-xs">Domena</TableHead><TableHead className="text-xs">IP</TableHead></TableRow></TableHeader>
              <TableBody>
                {selected.map((d) => {
                  const v = config.aRecord.ipOverrides[d.key] ?? d.hostingIp ?? "";
                  const bad = !!v && !isValidIPv4(v);
                  return (
                    <TableRow key={d.key}>
                      <TableCell className="font-mono text-xs py-1.5">{d.key}</TableCell>
                      <TableCell className="py-1.5">
                        <Input
                          value={v}
                          onChange={(e) => setIp(d.key, e.target.value)}
                          placeholder={d.ipAmbiguous ? `wybierz: ${d.candidateIps?.join(" / ")}` : "brak IP — wpisz"}
                          className={`font-mono text-xs h-7 ${bad ? "border-red-500" : ""}`}
                          list={d.candidateIps?.length ? `ips-${d.key}` : undefined}
                          disabled={!config.aRecord.enabled}
                        />
                        {d.candidateIps?.length ? <datalist id={`ips-${d.key}`}>{d.candidateIps.map((ip) => <option key={ip} value={ip} />)}</datalist> : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <p className="text-[11px] text-muted-foreground">IP z mapowania domena → hosting. Puste = rekord A pominięty dla tej domeny.</p>
        </CardContent>
      </Card>
    </div>
  );
};
